import { useTranslation } from "react-i18next";
import termsEn from "../../../../../../../content/legal/terms-en.md";
import termsEs from "../../../../../../../content/legal/terms-es.md";
import termsFr from "../../../../../../../content/legal/terms-fr.md";
import privacyEn from "../../../../../../../content/legal/privacy-en.md";
import privacyEs from "../../../../../../../content/legal/privacy-es.md";
import privacyFr from "../../../../../../../content/legal/privacy-fr.md";
import ContentMarkdown from "../../../ContentMarkdown";
import { PolicyTextStyle } from "../../../PolicyTextStyle";
import Loader from "../../../Loader";
import { Tabs } from "../../../ui/Tabs";
import { useActiveLang } from "../../../../domains/app/hooks/useActiveLang";
import type { SupportedLanguage } from "../../../../interfaces";
import { useMarkdown } from "../../../../domains/app/hooks/useMarkdown";
import {
  replaceSettingsView,
  type LegalDocTab,
} from "../../../../domains/app/stores/settingsSheetStore";
import { useApiQuery } from "../../../../services/api";
import { PartnerTermsContent } from "../../partners/PartnerTermsContent";
import { PartnersList } from "../../../partners/PartnersList";
import { SettingsSubHeader } from "../SettingsSubHeader";

// The terms and the privacy policy ship in English, French and Spanish; every
// other UI language reads the English copy (same rule as the legacy
// TermsPage / PolicyPage, which must stay in step with this table). The desktop
// settings read this table directly.
export const LEGAL_DOCS: Record<
  "terms" | "privacy",
  Partial<Record<SupportedLanguage, string>> & { en: string }
> = {
  terms: { en: termsEn, es: termsEs, fr: termsFr },
  privacy: { en: privacyEn, es: privacyEs, fr: privacyFr },
};

export function LegalView({
  doc,
  tab,
}: {
  doc: "terms" | "privacy";
  tab: LegalDocTab;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const source = LEGAL_DOCS[doc];
  const localized = source[activeLang];
  const url = localized === undefined ? source.en : localized;
  const { content } = useMarkdown(url);

  // The sheet only opens for authenticated users, so users/me is always
  // fetchable. Drives the Partners tab (partner T&Cs / marketing opt-ins).
  const userQuery = useApiQuery("/webapp/private/users/me", {
    query: { language: activeLang },
    keepPreviousData: true,
  });

  // Literal t() calls (the i18n scanner cannot extract t(variable)).
  const title = doc === "terms" ? t("Terms of use") : t("Privacy policy");

  // Parity with the legacy pages: /terms only surfaces the Partners tab when
  // one of the user's rooms ships partner T&Cs; /policy always shows it for
  // logged-in users (the opt-ins tab has its own empty state).
  const showTabs =
    doc === "privacy"
      ? true
      : userQuery.state === "success" &&
        userQuery.body.roomUserSettings.some(
          (setting) =>
            setting.room.termsConditions !== null &&
            setting.room.termsConditions.enabled === true
        );
  const activeTab: LegalDocTab = showTabs ? tab : "juisci";

  return (
    <div className="flex h-full flex-col">
      <SettingsSubHeader title={title} />
      {showTabs && (
        <Tabs
          tabs={[
            { value: "juisci", label: "Juisci", badge: null, icon: null },
            { value: "partners", label: "Partners", badge: null, icon: null },
          ]}
          activeTab={activeTab}
          onTabChange={(next) => {
            replaceSettingsView(
              doc === "terms"
                ? { view: "terms", tab: next }
                : { view: "privacy", tab: next }
            );
          }}
        />
      )}
      <div className="flex-1 overflow-y-auto scroll-fog-y px-5 pb-12">
        {activeTab === "partners" ? (
          <div className="pt-4">
            {doc === "privacy" ? (
              // The choice lives in "Partner sharing" (Figma O4): the Partners
              // tab of the privacy policy only lists the partners.
              <PartnersList layout="mobile" policies={null} />
            ) : userQuery.state === "success" ? (
              <PartnerTermsContent
                isLoading={userQuery.isFetching}
                roomUserSettings={userQuery.body.roomUserSettings}
              />
            ) : (
              <div className="flex justify-center py-10">
                <Loader />
              </div>
            )}
          </div>
        ) : content === null ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : (
          <PolicyTextStyle>
            <ContentMarkdown math={false} linkVariant="anchor">
              {content}
            </ContentMarkdown>
          </PolicyTextStyle>
        )}
      </div>
    </div>
  );
}
